/**
 * Checkout page.
 *
 * Loads the Razorpay JS SDK once, opens the Standard Checkout modal
 * with the order details we collected in ProductPage, and on success
 * POSTs the signed payload back to /storefront/orders/verify. On
 * verification success the user is sent to /thank-you/<token>.
 *
 * If the buyer navigated here directly (e.g. via refresh) we don't have
 * the order data in route state — show a "go back to landing" fallback.
 */
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';

import { useLang } from '@/i18n/useLang';
import { StorefrontError, storefrontApi } from '@/api/storefront';
import type { CreateOrderResponse } from '@/api/storefront';
import { StorefrontHeader } from '@/components/storefront/StorefrontHeader';

const RAZORPAY_SCRIPT_SRC = 'https://checkout.razorpay.com/v1/checkout.js';

interface RazorpaySuccess {
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
}

interface RazorpayWindow {
  Razorpay: new (opts: Record<string, unknown>) => { open: () => void };
}

export function CheckoutPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const { state } = useLocation() as { state?: { order?: CreateOrderResponse } };
  const navigate = useNavigate();
  const { lang } = useLang();
  const t = (hi: string, en: string) => (lang === 'hi' ? hi : en);
  const order = state?.order;

  const [scriptReady, setScriptReady] = useState(false);
  const [opened, setOpened] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const verifyOnceRef = useRef(false);

  // Load the Razorpay JS SDK on mount
  useEffect(() => {
    if (document.querySelector(`script[src="${RAZORPAY_SCRIPT_SRC}"]`)) {
      setScriptReady(true);
      return;
    }
    const script = document.createElement('script');
    script.src = RAZORPAY_SCRIPT_SRC;
    script.async = true;
    script.onload = () => setScriptReady(true);
    script.onerror = () =>
      setError(t('Razorpay लोड नहीं हो सका।', 'Could not load Razorpay.'));
    document.body.appendChild(script);
    // Intentionally don't remove on unmount — checkout.js is idempotent.
  }, [t]);

  const verifyMut = useMutation({
    mutationFn: async (s: RazorpaySuccess) => {
      if (!order) throw new Error('order missing');
      return storefrontApi.verifyPayment({
        order_id: order.order_id,
        razorpay_payment_id: s.razorpay_payment_id,
        razorpay_signature: s.razorpay_signature,
      });
    },
    onSuccess: (res) => {
      if (res.ok && res.public_token) {
        navigate(`/thank-you/${res.public_token}`, { replace: true });
      } else {
        setError(t('भुगतान सत्यापित नहीं हुआ।', 'Payment did not verify.'));
      }
    },
    onError: (err) => {
      if (err instanceof StorefrontError) setError(err.detail);
      else setError(String(err));
    },
  });

  // Auto-open the modal as soon as the SDK + order are both ready
  useEffect(() => {
    if (!scriptReady || !order || !order.razorpay_order_id || !order.razorpay_key_id) return;
    if (opened) return;
    setOpened(true);

    const rzpWindow = window as unknown as RazorpayWindow;
    const opts = {
      key: order.razorpay_key_id,
      amount: order.final_price_paise,
      currency: 'INR',
      order_id: order.razorpay_order_id,
      name: 'Institute of Financial Studies',
      description: order.report_slug,
      prefill: {
        name: order.buyer_name,
        email: order.buyer_email,
      },
      theme: { color: '#e87a1f' },
      handler: (resp: RazorpaySuccess) => {
        if (verifyOnceRef.current) return;
        verifyOnceRef.current = true;
        void verifyMut.mutateAsync(resp);
      },
      modal: {
        ondismiss: () => {
          setError(
            t(
              'भुगतान रद्द कर दिया गया। आप दोबारा कोशिश कर सकते हैं।',
              'Payment was cancelled. You can retry below.',
            ),
          );
          setOpened(false);
        },
      },
    };
    const rzp = new rzpWindow.Razorpay(opts);
    rzp.open();
  }, [scriptReady, order, opened, verifyMut, t]);

  if (!order) {
    return (
      <>
        <StorefrontHeader />
        <main className="mx-auto max-w-screen-md px-4 py-10 text-center">
          <p className="text-sm text-rose-300">
            {t(
              'चेकआउट विवरण उपलब्ध नहीं — कृपया फिर से शुरू करें।',
              'Checkout details missing — please start again.',
            )}
          </p>
          <button
            type="button"
            onClick={() => navigate('/')}
            className="mt-4 rounded-md border border-saffron-600 px-3 py-2 text-sm text-saffron-200 hover:bg-saffron-700/30"
          >
            {t('होम पर जाएँ', 'Back to home')}
          </button>
        </main>
      </>
    );
  }

  return (
    <>
      <StorefrontHeader />
      <main className="mx-auto max-w-screen-md px-4 py-10 text-center">
        <h1 className="text-2xl font-bold text-saffron-100">
          {t('भुगतान विंडो खुल रही है…', 'Opening payment window…')}
        </h1>
        <p className="mt-2 text-sm text-stone-400">
          {t('कुल देय: ', 'Amount due: ')}
          <span className="font-semibold text-saffron-200">
            {order.final_price_inr_display}
          </span>
        </p>
        <p className="mt-1 text-xs text-stone-500">
          {t(`आदेश संख्या #${orderId}`, `Order #${orderId}`)}
        </p>

        {!scriptReady && (
          <p className="mt-6 text-xs text-stone-500">
            {t('Razorpay लोड हो रहा है…', 'Loading Razorpay…')}
          </p>
        )}

        {error && (
          <div role="alert" className="mx-auto mt-6 max-w-sm rounded-md bg-rose-950/60 px-3 py-3 text-sm text-rose-200 ring-1 ring-rose-900">
            {error}
          </div>
        )}

        {(error || verifyMut.isError) && scriptReady && (
          <button
            type="button"
            onClick={() => { setError(null); setOpened(false); }}
            className="mt-4 rounded-md bg-saffron-600 px-4 py-2 text-sm font-semibold text-stone-950 hover:bg-saffron-500"
          >
            {t('पुनः प्रयास करें', 'Retry payment')}
          </button>
        )}

        {verifyMut.isPending && (
          <p className="mt-6 text-sm text-stone-400">
            {t('भुगतान सत्यापित किया जा रहा है…', 'Verifying payment…')}
          </p>
        )}
      </main>
    </>
  );
}
